import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PlusIcon, TrashIcon, PencilIcon } from "@heroicons/react/24/outline";
import { PMButton } from "../../../utils/PMButton";
import { AddSubDialog } from "./add-sub-dialog";
import { CreateReportsDialog } from "./create-reports-dialog";
import axios from "../../../../api/axios";
import { toast } from "react-hot-toast";
import { runCreateReportsOperation } from "./runCreateReportsOperation";

export const ServiceAgreementSubs = ({ agreement, onRefresh }) => {
  const { t } = useTranslation();
  const currentCompany = useSelector((state) => state.session?.currentCompany);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addSubDialogOpen, setAddSubDialogOpen] = useState(false);
  const [createReportsDialogOpen, setCreateReportsDialogOpen] = useState(false);
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [editingSub, setEditingSub] = useState(null);

  useEffect(() => {
    if (agreement?.id) {
      fetchSubs();
    }
  }, [agreement?.id]);

  const fetchSubs = async () => {
    if (!agreement?.id) return;
    setLoading(true);
    try {
      const response = await axios.get(`/service/agreements/${agreement.id}/subs`);
      setSubs(response.data.data || []);
    } catch (error) {
      toast.error(t("service.agreements.subs.fetch_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddSub = async (subData) => {
    if (editingSub) {
      const response = await axios.put(`/service/agreements/${agreement.id}/subs/${editingSub.id}`, { sub: subData });
      if (!response.data?.id) throw new Error(t("service.agreements.subs.save_failed"));
      setSubs((previous) => previous.map((row) => row.id === editingSub.id ? response.data : row));
      setAddSubDialogOpen(false);
      setEditingSub(null);
      toast.success(t("service.agreements.subs.saved"));
      return;
    }
    const response = await axios.post(`/service/agreements/${agreement.id}/subs`, {
      sub: subData,
    });
    if (response.status === 201) {
      toast.success(t("service.agreements.subs.added"));
      setAddSubDialogOpen(false);
      fetchSubs();
      if (onRefresh) onRefresh(agreement);
      return;
    }
    throw new Error("Unexpected response status");
  };

  const handleDeleteSub = async (subId) => {
    if (!window.confirm(t("service.agreements.subs.delete_confirm"))) return;
    try {
      const response = await axios.delete(
        `/service/agreements/${agreement.id}/subs/${subId}`
      );
      if (response.status === 200) {
        toast.success(t("service.agreements.subs.deleted"));
        fetchSubs();
        if (onRefresh) onRefresh(agreement);
      }
    } catch (error) {
      toast.error(t("service.agreements.subs.delete_failed"));
    }
  };

  const handleSubSelection = (subId, isSelected) => {
    setSelectedSubs((prev) =>
      isSelected ? [...prev, subId] : prev.filter((id) => id !== subId)
    );
  };

  const handleSelectAll = () => {
    if (selectedSubs.length === subs.length) {
      setSelectedSubs([]);
    } else {
      setSelectedSubs(subs.map((sub) => sub.id));
    }
  };

  const handleCreateReports = async (reportSettings) => {
    // Close settings dialog first — OperationActionDialog owns feedback.
    setCreateReportsDialogOpen(false);
    const result = await runCreateReportsOperation({
      agreement,
      selectedSubs,
      subs,
      settings: reportSettings,
      companyId: currentCompany?.id,
      onRefresh: async () => {
        await fetchSubs();
        if (onRefresh) onRefresh(agreement);
      },
      t,
    });
    if (result.ok) {
      setSelectedSubs([]);
    }
  };

  if (loading) {
    return (
      <div className="rounded-[18px] border border-slate-200 bg-white p-5">
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-1/3 rounded bg-slate-200" />
          <div className="h-4 rounded bg-slate-200" />
        </div>
      </div>
    );
  }

  if (!agreement?.id) return null;

  return (
    <>
      <section className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.06)]">
        <div className="flex flex-col gap-3 border-b border-slate-100 bg-[#EAF3FD] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-[15px] font-extrabold text-slate-900">
              {t("service.agreements.subs.title")} ({subs.length})
            </h2>
            <p className="text-sm text-slate-600">
              {t("service.agreements.subs.help")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {subs.length > 0 && (
              <PMButton
                onClick={() => setCreateReportsDialogOpen(true)}
                disabled={selectedSubs.length === 0}
              >
                {t("service.agreements.subs.create_jobs", {
                  count: selectedSubs.length,
                })}
              </PMButton>
            )}
            <PMButton onClick={() => { setEditingSub(null); setAddSubDialogOpen(true); }}>
              <PlusIcon className="mr-2 h-4 w-4" />
              {t("service.agreements.subs.add")}
            </PMButton>
          </div>
        </div>

        {subs.length > 0 ? (
          <div className="overflow-x-auto px-5 py-4">
            <label className="mb-3 flex items-center text-sm text-slate-700">
              <input
                type="checkbox"
                checked={selectedSubs.length === subs.length && subs.length > 0}
                onChange={handleSelectAll}
                className="rounded border-slate-300 text-[#1463C7]"
              />
              <span className="ml-2">{t("service.agreements.subs.select_all")}</span>
            </label>
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-slate-500">
                  <th className="pb-2 pr-3" />
                  <th className="pb-2 pr-3">{t("service.agreements.subs.col_location")}</th>
                  <th className="pb-2 pr-3">{t("service.agreements.subs.col_equipment")}</th>
                  <th className="pb-2 pr-3">{t("service.agreements.subs.col_designation")}</th>
                  <th className="pb-2 pr-3">{t("service.agreements.subs.col_filter")}</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {subs.map((sub) => (
                  <tr key={sub.id} className="align-top">
                    <td className="py-3 pr-3">
                      <input
                        type="checkbox"
                        checked={selectedSubs.includes(sub.id)}
                        onChange={(e) =>
                          handleSubSelection(sub.id, e.target.checked)
                        }
                        className="rounded border-slate-300 text-[#1463C7]"
                      />
                    </td>
                    <td className="py-3 pr-3 text-slate-700">
                      {sub.location_label ||
                        sub.full_address ||
                        sub.delivery_address?.displayTitle ||
                        "—"}
                    </td>
                    <td className="py-3 pr-3 text-slate-700">
                      {sub.equipment_name || sub.system?.displayTitle || "—"}
                    </td>
                    <td className="py-3 pr-3 font-medium text-slate-900">
                      {sub.designation || sub.name || sub.customer?.name || "—"}
                    </td>
                    <td className="max-w-[16rem] py-3 pr-3 text-slate-600">
                      {sub.filter_spec || sub.description || "—"}
                    </td>
                    <td className="py-3 text-right">
                      <button type="button" onClick={() => { setEditingSub(sub); setAddSubDialogOpen(true); }} className="rounded-full p-2 text-blue-700 hover:bg-blue-50" title={t("service.agreements.subs.edit")}>
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSub(sub.id)}
                        className="rounded-full p-2 text-rose-600 hover:bg-rose-50"
                        title={t("service.agreements.subs.delete")}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-5 py-8 text-center text-sm text-slate-500">
            {t("service.agreements.subs.empty")}
          </div>
        )}
      </section>

      {addSubDialogOpen && <AddSubDialog
        key={editingSub?.id || "new"}
        sub={editingSub}
        open={addSubDialogOpen}
        onClose={() => setAddSubDialogOpen(false)}
        onSubmit={handleAddSub}
        agreement={agreement}
      />}

      <CreateReportsDialog
        open={createReportsDialogOpen}
        onClose={() => setCreateReportsDialogOpen(false)}
        onSubmit={handleCreateReports}
        selectedSubsCount={selectedSubs.length}
        agreement={agreement}
      />
    </>
  );
};
